// =============================================
// Accept Invitation Page - קבלת הזמנה
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserPlus,
  Users,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Baby,
  ArrowRight,
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { parentColors } from '../../styles/colors';

const colors = {
  ...parentColors,
  background: '#f9fafb',
};

interface InvitationDetails {
  token: string;
  inviterName: string;
  relationType: string;
  children: {
    publicId: string;
    firstName: string;
    lastName: string;
    kindergartenName?: string;
  }[];
  expiresAt: string;
}

export default function AcceptInvitation() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { token: authToken, isAuthenticated, user } = useAuthStore();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<'accepted' | 'declined' | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('קישור הזמנה לא תקין');
      setLoading(false);
      return;
    }

    // First check if user is authenticated
    if (!isAuthenticated || !authToken) {
      // Redirect to login with the token (login will redirect to registration if needed)
      navigate(`/parent/login?redirect=/parent/invitation/${token}`);
      return;
    }

    fetchInvitationDetails();
  }, [token, isAuthenticated, authToken]);

  const fetchInvitationDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get pending invitations for this user's phone
      const res = await fetch(`${API_BASE_URL}/invitations/pending?phone=${user?.phone}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Handle unauthorized - token might be expired
      if (res.status === 401) {
        setNeedsLogin(true);
        setLoading(false);
        return;
      }

      const data = await res.json();

      if (data.success && data.data) {
        // Find the invitation with matching token
        const inv = data.data.find((i: InvitationDetails) => i.token === token);
        if (inv) {
          setInvitation(inv);
        } else {
          setError('ההזמנה לא נמצאה או שתוקפה פג');
        }
      } else {
        setError(data.message || 'שגיאה בטעינת פרטי ההזמנה');
      }
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('שגיאה בטעינת פרטי ההזמנה');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token || !authToken) {
      setNeedsLogin(true);
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/invitations/${token}/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Handle unauthorized - token might be expired
      if (res.status === 401) {
        setNeedsLogin(true);
        setProcessing(false);
        return;
      }

      const data = await res.json();

      if (data.success) {
        setSuccess('accepted');
        // Redirect to children page after 2 seconds
        setTimeout(() => {
          navigate('/parent/children');
        }, 2000);
      } else {
        setError(data.message || 'שגיאה באישור ההזמנה');
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError('שגיאה באישור ההזמנה');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token || !authToken) {
      setNeedsLogin(true);
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const res = await fetch(`${API_BASE_URL}/invitations/${token}/decline`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Handle unauthorized - token might be expired
      if (res.status === 401) {
        setNeedsLogin(true);
        setProcessing(false);
        return;
      }

      const data = await res.json();

      if (data.success) {
        setSuccess('declined');
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/parent/dashboard');
        }, 2000);
      } else {
        setError(data.message || 'שגיאה בדחיית ההזמנה');
      }
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError('שגיאה בדחיית ההזמנה');
    } finally {
      setProcessing(false);
    }
  };

  const getRelationLabel = (type: string) => {
    const labels: Record<string, string> = {
      PayingParent: 'הורה משלם',
      Parent: 'הורה',
      Contact: 'איש קשר',
      Grandparent: 'סב/סבתא',
      Nanny: 'מטפלת',
      Other: 'אחר',
    };
    return labels[type] || type;
  };

  const handleLoginRedirect = () => {
    // Clear expired auth state and redirect to login
    navigate(`/parent/login?redirect=/parent/invitation/${token}`);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader2 size={48} color="white" className="animate-spin" />
      </div>
    );
  }

  // Needs login state - token expired or invalid
  if (needsLogin) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            width: '100%',
            maxWidth: '400px',
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '32px',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: colors.warningLight,
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={40} color={colors.warning} />
          </div>

          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '12px',
            }}
          >
            נדרשת התחברות
          </h1>

          <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '24px' }}>
            כדי לאשר את ההזמנה, יש להתחבר למערכת
          </p>

          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleLoginRedirect}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              backgroundColor: colors.primary,
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <ArrowRight size={18} />
            התחבר עכשיו
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            width: '100%',
            maxWidth: '400px',
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '40px 32px',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: success === 'accepted' ? colors.successLight : colors.warningLight,
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {success === 'accepted' ? (
              <CheckCircle2 size={40} color={colors.success} />
            ) : (
              <XCircle size={40} color={colors.warning} />
            )}
          </motion.div>

          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '12px',
            }}
          >
            {success === 'accepted' ? 'ההזמנה אושרה!' : 'ההזמנה נדחתה'}
          </h1>

          <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '24px' }}>
            {success === 'accepted'
              ? 'הילדים נוספו לרשימת הילדים שלך. מעביר אותך למסך הילדים...'
              : 'מעביר אותך למסך הבית...'}
          </p>

          <Loader2 size={24} color={colors.primary} className="animate-spin" style={{ margin: '0 auto' }} />
        </motion.div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            width: '100%',
            maxWidth: '400px',
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '32px',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: colors.dangerLight,
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={40} color={colors.danger} />
          </div>

          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '12px',
            }}
          >
            שגיאה
          </h1>

          <p style={{ fontSize: '14px', color: colors.textLight, marginBottom: '24px' }}>
            {error}
          </p>

          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/parent/dashboard')}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              backgroundColor: colors.primary,
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <ArrowRight size={18} />
            חזרה למסך הבית
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Main invitation view
  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.primary} 0%, #ea580c 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          width: '100%',
          maxWidth: '450px',
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              backgroundColor: colors.primaryLight,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UserPlus size={40} color={colors.primary} />
          </div>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '8px',
            }}
          >
            הזמנה להצטרפות
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {invitation?.inviterName} מזמין אותך להצטרף כ{getRelationLabel(invitation?.relationType || '')}
          </p>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{
                backgroundColor: colors.dangerLight,
                color: colors.danger,
                padding: '12px 16px',
                borderRadius: '12px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Children List */}
        {invitation && invitation.children.length > 0 && (
          <div
            style={{
              backgroundColor: colors.background,
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
                color: colors.text,
                fontWeight: '600',
              }}
            >
              <Users size={18} />
              עבור הילדים הבאים:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {invitation.children.map((child) => (
                <div
                  key={child.publicId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      backgroundColor: colors.secondaryLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Baby size={24} color={colors.secondary} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '500', color: colors.text }}>
                      {child.firstName} {child.lastName}
                    </div>
                    {child.kindergartenName && (
                      <div style={{ fontSize: '12px', color: colors.textLight }}>
                        {child.kindergartenName}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div
          style={{
            backgroundColor: colors.primaryLight,
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '24px',
            fontSize: '13px',
            color: colors.text,
            lineHeight: '1.6',
          }}
        >
          <strong>מה קורה אחרי אישור?</strong>
          <ul style={{ margin: '8px 0 0', paddingRight: '18px' }}>
            <li>הילדים יתווספו לרשימת הילדים שלך</li>
            <li>תוכל לראות את סטטוס הנוכחות שלהם</li>
            <li>תקבל התראות לפי הגדרות ההורה המשלם</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleDecline}
            disabled={processing}
            style={{
              flex: 1,
              padding: '14px',
              fontSize: '15px',
              fontWeight: '600',
              color: colors.danger,
              backgroundColor: colors.dangerLight,
              border: 'none',
              borderRadius: '12px',
              cursor: processing ? 'not-allowed' : 'pointer',
              opacity: processing ? 0.7 : 1,
            }}
          >
            דחה
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleAccept}
            disabled={processing}
            style={{
              flex: 2,
              padding: '14px',
              fontSize: '15px',
              fontWeight: '600',
              color: 'white',
              backgroundColor: colors.success,
              border: 'none',
              borderRadius: '12px',
              cursor: processing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: processing ? 0.7 : 1,
            }}
          >
            {processing ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                <CheckCircle2 size={20} />
                אשר הזמנה
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
